import { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import PropTypes from "prop-types";

import { ReactComponent as ClearIcon } from "../../../../assets/icons/clear.svg";
import { BULK_ACCOUNT_PLACEHOLDER } from "../../../../constants/strings";
import { MAX_BULK_CHAT_QUEUE_ACCOUNTS } from "../../../../features/customerSettings/customerSettings.constants";
import {
  isBulkAccountNumber,
  mergeBulkAccountNumbers,
  parseBulkAccountNumbers,
} from "../../../../features/customerSettings/customerSettings.models";
import styles from "./AccountChipField.module.scss";

const SEPARATOR_KEYS = ["Enter", ",", ";", " "];

const AccountChipField = ({
  label,
  input,
  meta,
  showMetaErrors,
  maxAccounts,
}) => {
  const inputRef = useRef(null);
  const [draft, setDraft] = useState("");
  const accounts = parseBulkAccountNumbers(input.value);
  const displayMetaError =
    showMetaErrors &&
    !meta.valid &&
    (meta.dirty || meta.touched) &&
    !meta.active;

  useEffect(() => {
    if (meta.pristine) {
      setDraft("");
    }
  }, [meta.pristine]);

  const commitValue = (value) => {
    input.onChange(mergeBulkAccountNumbers(accounts, value, maxAccounts));
    setDraft("");
  };

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const commitDraft = () => {
    if (!draft.trim()) {
      return;
    }

    commitValue(draft);
  };

  const removeAccount = (accountToRemove) => {
    input.onChange(accounts.filter((account) => account !== accountToRemove));
    focusInput();
  };

  const clearAccounts = (event) => {
    event.stopPropagation();
    input.onChange([]);
    setDraft("");
    focusInput();
  };

  const handleKeyDown = (event) => {
    if (SEPARATOR_KEYS.includes(event.key)) {
      event.preventDefault();
      commitDraft();
      return;
    }

    if (event.key === "Backspace" && !draft && accounts.length) {
      event.preventDefault();
      removeAccount(accounts[accounts.length - 1]);
    }
  };

  const handlePaste = (event) => {
    const pastedValue = event.clipboardData.getData("text");

    if (
      !/[\s,;]/.test(pastedValue) &&
      parseBulkAccountNumbers(pastedValue).length < 2
    ) {
      return;
    }

    event.preventDefault();
    commitValue(`${draft}${pastedValue}`);
  };

  const handleChange = (event) => {
    const nextDraft = event.target.value.replace(/[^\d]/g, "").slice(0, 7);

    if (nextDraft.length === 7) {
      commitValue(nextDraft);
      return;
    }

    setDraft(nextDraft);
  };

  const handleBlur = () => {
    if (draft.trim()) {
      const nextAccounts = mergeBulkAccountNumbers(
        accounts,
        draft,
        maxAccounts
      );
      setDraft("");
      input.onChange(nextAccounts);
      input.onBlur(nextAccounts);
      return;
    }

    input.onBlur(accounts);
  };

  return (
    <>
      <div
        className={classNames(
          styles.field,
          displayMetaError && styles.metaError
        )}
        onClick={focusInput}
      >
        {label && <div className={styles.label}>{`${label}:`}</div>}
        <div className={styles.chips}>
          {accounts.map((account) => (
            <span
              key={account}
              className={classNames(
                styles.chip,
                !isBulkAccountNumber(account) && styles.invalid
              )}
            >
              {account}
              <button
                type="button"
                className={styles.remove}
                aria-label={`Remove ${account}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation();
                  removeAccount(account);
                }}
              >
                <ClearIcon className={styles.removeIcon} />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            className={styles.input}
            value={draft}
            placeholder={accounts.length ? "" : BULK_ACCOUNT_PLACEHOLDER}
            autoComplete="off"
            onFocus={input.onFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        </div>
        {!!accounts.length && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear all accounts"
            onMouseDown={(event) => event.preventDefault()}
            onClick={clearAccounts}
          >
            <ClearIcon className={styles.removeIcon} />
          </button>
        )}
      </div>
      <div className={styles.footer}>
        {`${accounts.length} / ${maxAccounts}`}
      </div>
    </>
  );
};

AccountChipField.propTypes = {
  label: PropTypes.string,
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  showMetaErrors: PropTypes.bool,
  maxAccounts: PropTypes.number,
};

AccountChipField.defaultProps = {
  showMetaErrors: false,
  maxAccounts: MAX_BULK_CHAT_QUEUE_ACCOUNTS,
};

export default AccountChipField;
